import React from "react";
import { X } from "lucide-react";

const SlabTable = ({ priceList, multiple, currency, selectedSlab, badge }) => (
  <table className="min-w-full text-sm">
    <thead className="bg-gray-50 text-gray-600">
      <tr>
        {["Min Qty", "Multiple", "Unit Price", "Ext Price"].map((col) => (
          <th
            key={col}
            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide ${
              col === "Unit Price" || col === "Ext Price" ? "text-right" : "text-left"
            }`}
          >
            {col}
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100">
      {(priceList || []).map((slab, i) => {
        const isSelected =
          slab.min_qty === selectedSlab?.min_qty &&
          (slab.price === selectedSlab?.price || slab.actual_price === selectedSlab?.actual_price);
        const unitPrice = slab.price ?? slab.actual_price;
        const extPrice =
          unitPrice != null && slab.min_qty != null
            ? (parseFloat(unitPrice) * slab.min_qty).toFixed(2)
            : "-";
        return (
          <tr
            key={i}
            className={isSelected ? "bg-s360-violet-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50"}
          >
            <td className="px-4 py-2.5 font-medium">
              {slab.min_qty ?? "-"}
              {isSelected && (
                <span className="ml-2 text-xs bg-s360-violet-50 text-s360-violet-600 px-1.5 py-0.5 rounded font-semibold">
                  {badge ?? "selected"}
                </span>
              )}
            </td>
            <td className="px-4 py-2.5">{multiple ?? "-"}</td>
            <td className="px-4 py-2.5 text-right font-semibold">{unitPrice ?? "-"}</td>
            <td className="px-4 py-2.5 text-right font-medium">{extPrice}</td>
            {/* <td className="px-4 py-2.5">{currency ?? "-"}</td> */}
          </tr>
        );
      })}
    </tbody>
  </table>
);

const SectionHeader = ({ children }) => (
  <div className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-t flex items-center gap-2">
    {children}
  </div>
);

const BomPriceSlabModal = ({ product, packageOptions, onClose, pricingBasis, stockQty }) => {
  if (!product) return null;

  const isSplit = !!product.isSplitOrder;
  const hasPackageOptions =
    !isSplit && Array.isArray(packageOptions) && packageOptions.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Price Slabs{isSplit && <span className="ml-2 text-xs bg-s360-violet-50 text-s360-violet-600 px-2 py-0.5 rounded">Split Order</span>}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {product.part_number} &bull; {product.distributor} &bull;{" "}
              {product.manufacturer}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[60vh]">
          {isSplit ? (
            <>
              <SectionHeader>
                {product.splitSecond?.product?.price?.package_type || "Cut Tape (CT) & Digi-Reel®"} · {product.splitSecond?.qty?.toLocaleString()} units @ {product.splitSecond?.product?.price?.currency} {product.splitSecond?.unitPrice}
              </SectionHeader>
              <div className="overflow-x-auto">
                <SlabTable
                  priceList={product.splitSecond?.product?.price?.price_list}
                  multiple={product.splitSecond?.product?.price?.multiple}
                  currency={product.splitSecond?.product?.price?.currency}
                  selectedSlab={product.splitSecond?.slab}
                  badge={`CT · ${product.splitSecond?.qty?.toLocaleString()} units`}
                />
              </div>
              <SectionHeader>
                Tape & Reel (TR) · {product.splitFirst?.qty?.toLocaleString()} units @ {product.price?.currency} {product.splitFirst?.unitPrice}
              </SectionHeader>
              <div className="overflow-x-auto">
                <SlabTable
                  priceList={product.price?.price_list}
                  multiple={product.price?.multiple}
                  currency={product.price?.currency}
                  selectedSlab={product.splitFirst?.slab}
                  badge={`TR · ${product.splitFirst?.qty?.toLocaleString()} units`}
                />
              </div>
              <div className="px-5 py-3 border-t border-gray-200 bg-s360-violet-50 text-xs text-s360-violet-600 font-medium">
                Combined: {product.actualOrderQty?.toLocaleString()} units · Avg unit price {product.price?.currency} {product.selectedPrice?.price}
              </div>
            </>
          ) : hasPackageOptions ? (
            <>
              {packageOptions.map((opt, i) => {
                const labels = opt.mergedLabels?.filter(Boolean)?.length
                  ? opt.mergedLabels.filter(Boolean)
                  : [opt.price?.package_type || "Standard"];
                const headerLabel = labels.join(" & ");
                const isActive =
                  labels.includes(product.price?.package_type ?? "") ||
                  (opt.price?.package_type ?? "") ===
                    (product.price?.package_type ?? "");
                return (
                  <div key={`${headerLabel}-${i}`}>
                    <SectionHeader>
                      {headerLabel}
                      {isActive && (
                        <span className="text-[10px] normal-case bg-s360-violet-50 text-s360-violet-600 px-1.5 py-0.5 rounded font-semibold">
                          Selected
                        </span>
                      )}
                    </SectionHeader>
                    <div className="overflow-x-auto">
                      <SlabTable
                        priceList={opt.price?.price_list}
                        multiple={opt.price?.multiple}
                        currency={opt.price?.currency}
                        selectedSlab={isActive ? opt.selectedPrice : null}
                      />
                    </div>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="overflow-x-auto">
              <SlabTable
                priceList={product.price?.price_list}
                multiple={product.price?.multiple}
                currency={product.price?.currency}
                selectedSlab={product.selectedPrice}
              />
            </div>
          )}
        </div>

        {!isSplit && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs flex items-center justify-between">
            {pricingBasis === "stock" && stockQty ? (
              <span className="text-s360-warning font-medium">
                Priced for available stock: {stockQty.toLocaleString()} units
              </span>
            ) : pricingBasis === "rfq" ? (
              <span className="text-s360-info font-medium">
                Priced for RFQ qty
              </span>
            ) : (
              <span />
            )}
            <span className="text-gray-500">Click outside or press &times; to close</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BomPriceSlabModal;
